/**
 * Highlights page.
 *
 * @module app/pages/HighlightsPage
 */
import _ from 'lodash';
import BasePage from './BasePage';
import BodyStateAdapter from '../components/BodyStateAdapter';
import EventViewAdapter from '../views/EventViewAdapter';
import FirstTimeUserJourney from '../components/FirstTimeUserJourney';
import SelectionViewAdapter from '../views/SelectionViewAdapter';
import Tabs from '../components/Tabs';
import LivescoreDiffusionAdapter from '../components/push/LivescoreDiffusionAdapter';
import { renderNewBanners } from '../components/newBanners/newBanners';
import { tryRenderStories, STORYLY_SUPPORTED_PAGES } from '../components/stories';
import EnhancedOddsDiffusionAdapter from '../components/push/EnhancedOddsDiffusionAdapter';
/**
 * Internal state flag used to
 * determine whether page has been
 * rendered or not
 *
 * @type {Boolean}
 */
var rendered = false;

/**
 * Page content in the form of a jQuery element
 *
 * @type {Object}
 */
var content = null;

/**
 * Page dependencies
 *
 * @type {Object}
 */
var deps = null;

/**
 * Page arguments
 *
 * @type {Object}
 */
var args = null;

/**
 * Function used to bind event handles to
 * various buttons/links on the page
 *
 * @param   {Object} content jQuery object containing
 *                           the page content
 * @param   {Object} deps    Page dependencies
 *
 * @returns {void}
 *
 * @private
 */
var bindNavigation = function (pageContent, pageDeps, pageArgs) {

    content = pageContent;
    deps = pageDeps;
    args = pageArgs;

    showMatches('.navMatches');
};

/**
 * Function used to initialize views
 * on the page
 *
 * @param   {Object} deps    Page dependencies
 * @param   {Object} content jQuery element that contains the page
 *
 * @returns {void}
 *
 * @private
 */
var initViews = function () {

    var model = deps.sportsDataRepository.getModel();

    // Subscribe live events to live score updates
    _.each(model.events, subscribeToLivescore);
    _.each(model.events, subscribeToEnhancedOdds);
    BasePage.setOddsFormat();



    if(window.WH.areNewBannersOnHighlightsEnabled) {
        renderNewBanners('highlights');
    }

};

/**
 * Makes a DOM element visible
 *
 * @param   {String | Object}   selector    css selector string
 *                                          or jquery object
 *
 * @returns {void}
 *
 * @private
 */
var showMatches = function (selector) {

    if (window.WH.sportsbook.showMatches) {
        BasePage.jQuery(selector).removeClass('displayNone');
    }
};

/**
 * Subscribe an in play event to livescore
 * updates
 *
 * @param   {Object} event   Event model
 * @param   {String} eventId Event ID
 *
 * @returns {void}
 *
 * @private
 */
var subscribeToLivescore = function (event, eventId) {

    if (event.isInPlay) {
        LivescoreDiffusionAdapter.subscribe(
            BasePage.getSportEnSlug(event),
            eventId,
            EventViewAdapter.livescore(eventId, content).update
        );
    }
};

/**
 * Subscribe to enhanced dds was price
 * updates
 *
 * @param   {Object} event   Event model
 * @param   {String} eventId Event ID
 *
 * @returns {void}
 *
 * @private
 */
var subscribeToEnhancedOdds = function (event, eventId) {

    if (event.hasEnhancedOdds) {
        EnhancedOddsDiffusionAdapter.subscribe(
            eventId,
            EventViewAdapter.wasPrice(eventId, content).update
        );
    }
};

/**
 * Subscribe a set of entities (events, markets, etc)
 * to PUSH updates
 *
 * @param   {Object} set An object containing entities
 *                       such as markets, selections, events
 *
 * @returns {void}
 *
 * @private
 */
var subscribeSetToPush = function (set) {

    _.each(set, subscribeEntityToPush);
};

/**
 * Subscribe a single entity to PUSH updates
 *
 * @param   {Object} entity Market/event/selection model
 *
 * @returns {void}
 *
 * @private
 */
var subscribeEntityToPush = function (entity) {

    if (entity && entity.id && !entity.id.match(/OB_TY/g) &&
        deps.pageCommon.DomPushAdapter.subscribe(entity, push.callback)) {

        deps.pageCommon.PushAdapter.subscribe(entity.pdsTopic);
    }
};


/**
 * Method to tidy up anything on the highlights page after a push call
 * currently just used to check if there are no more pre-match events
 * so that we can remove the title
 *
 * @returns {void}
 *
 * @private
 */
var removeEmptyPrematchArea = function () {

    //get the count of prematch events on the page
    var preMatchEventCount = BasePage.jQuery('#match-highlights .event').length;

    if (preMatchEventCount === 0) {
        //as there are no pre match events, hide the pre match events container
        BasePage.jQuery('#match-highlights').addClass('displayNone');
    }
};

/**
 * Remove a market group associate to an event if only has 1 child
 *
 * @param  {Object} DOM event child of the Market Group
 *
 * @returns {void}
 *
 * @private
 */
var removeMarketGroup = function ($grouper) {

    if ($grouper.parent().children().length === 1) {
        removeMarketGroupFromDom($grouper);
    }
};

/**
 * Remove a market group associate to an event
 * currently just used to check if there are no more pre-match events
 * so that we can remove the title
 *
 * @param  {Object} DOM event child of the Market Group
 *
 * @returns {void}
 *
 * @private
 */
var removeMarketGroupFromDom = function ($grouper) {

    var $eventContainer = $grouper.parents('.highlightsmarket');

    if ($eventContainer.length === 1) {
        var parentEventContainer = $eventContainer.parent();
        $eventContainer.prev().remove();
        $eventContainer.remove();
        removeAllTemplate(parentEventContainer);
    }
};

/**
 * Remove a all matches button from the dom if in play section is empty
 * currently just used to check if there are no more pre-match events
 * so that we can remove the title
 *
 * @param  {Object} DOM element child of the template to be removed
 *
 * @returns {void}
 *
 * @private
 */
var removeAllTemplate = function ($grouper) {

    if ($grouper.children().length === 1) {
        $grouper.parents('#in-play-now').remove();
    }
};

/**
 * Removes a market header
 * When there's only one event left inside a market group,
 * when this event is removed the market header must be erased
 *
 * @returns {void}
 *
 * @private
 */
var removeMarketHeader = function ($grouper) {
    var $events = $grouper.find('.event:visible');

    if (!hasVisibleEvents($events)) {
        $grouper.remove();
    }
};

/**
 * Checks if the array of events passed contains any visible event
 *
 * @param {Array} $events Array of event HTML elements
 *
 * @returns {boolean} true if there's at least one event visible
 *
 * @private
 */
var hasVisibleEvents = function ($events) {

    return _.some($events, isVisibleEvent);
};

/**
 * Checks if the passed element is visible
 *
 * @param {HTMLElement} event Event element
 *
 * @returns {boolean}
 *
 * @private
 */
var isVisibleEvent = function (event) {

    return !event.classList.contains('displayNone');
};

/**
 * Removes market header and pre match area container
 * if needed
 *
 * @param   {Object} message  DOM PUSH message
 * @param   {Object} $grouper jQuery element
 *
 * @returns {void}
 *
 * @private
 */
var removeObsoleteElements = function (message, $grouper) {

    if (message.value) {
        removeMarketHeader($grouper);
        removeEmptyPrematchArea();
    }
};

/**
 * Removes an element if possible
 *
 * @param   {Object} $entity jQuery element
 *
 * @returns {void}
 *
 * @private
 */
var removeEntity = function ($entity) {

    if ($entity.length > 0) {
        $entity.remove();
    }
};

/**
 * Function used to show the alternate handicap
 *
 * @param {Object} $event jQuery element for the event
 *
 * @return {void}
 *
 * @private
 */
var showAlternateHandicap = function showAlternateHandicap($event) {

    var handicapElement = $event.find('.selectionhandicap');

    if (handicapElement.length > 0) {

        var alternateHandicap = $event.find('.alternate-market').attr('data-hcapvalue');
        handicapElement.text(alternateHandicap);
    }
};

/**
 * Function used to display the livescore for an event
 * based on event element & event id
 *
 * @param  {Object} $event  jQuery element for the event
 * @param  {String} eventId Event ID
 *
 * @return {void}
 *
 * @private
 */
var showLivescore = function ($event, eventId) {

    $event.find('time').addClass('displayNone');

    if ($event.hasClass('markettable__event')) {

        $event.find('.area-livescore .event__status').removeClass('displayNone');
    } else {

        $event.find('.wh-label:first').removeClass('displayNone');
        showAlternateHandicap($event);
    }

    LivescoreDiffusionAdapter.subscribe(
        BasePage.getSportEnSlug($event),
        eventId,
        EventViewAdapter.livescore(eventId, content).update
    );
};

/**
 * Function used to display the start time for an event
 * based on event element & event id
 *
 * @param  {Object} $event  jQuery element for the event
 * @param  {String} eventId Event ID
 *
 * @return {void}
 *
 * @private
 */
var showStartTime = function ($event, eventId) {

    $event.find('.area-livescore time').removeClass('displayNone');
    $event.find('.area-livescore .event__status').addClass('displayNone');
    LivescoreDiffusionAdapter.unsubscribe(eventId);
};

/**
 * Hide an event if it's just been pushed to
 * isInPlay = true
 *
 * @param  {Object} $event  jQuery element for the event
 * @param  {Object} message PUSH message
 *
 * @return {void}
 *
 * @private
 */
var hideInPlayEvent = function ($event, message) {
    // Business question: do we want to hide only in play?
    //if (message.value === true) {
    var $grouper = $event.parents('.header-events-grouper');
    removeEntity($event);
    removeObsoleteElements(message, $grouper);
    //}
};

/**
 * Show the alternate market for an event:
 * If the event has been pushed to isInPlay=true,
 * then hide the pre match market and display the
 * in play market.
 * If the event has been pushed to isInPlay=false,
 * then hide the in play market and display the pre match
 * market.
 *
 * @param  {Object} $event  jQuery element for the event
 * @param  {Object} message PUSH message
 *
 * @return {void}
 *
 * @private
 */
var showAlternateMarket = function ($event, message) {

    if ($event.find('[data-displayed=true][data-betinrun=' + (message.value) + ']').length > 0) {
        $event.find('[data-betinrun=' + (message.value) + ']').removeClass('displayNone');
        $event.find('[data-betinrun=' + (!message.value) + ']').addClass('displayNone');
    } else {

        var $grouper = $event.parents('.header-events-grouper');
        removeEntity($event);
        removeObsoleteElements(message, $grouper);
    }
};

/**
 * Show livescore or start time, based on isInPlay status
 * received from a PUSH message
 *
 * @param  {Object} $event  jQuery element for the event
 * @param  {Object} message PUSH message
 *
 * @return {void}
 *
 * @private
 */
var handleLivescore = function ($event, message) {

    if (message.value === true) {
        showLivescore($event, message.id);
    } else {
        showStartTime($event, message.id);
    }
};

/**
 * Push related functionality
 *
 * @type {Object}
 */
var push = {

    /**
     * DOM PUSH update callback function
     * This function is called every time a push
     * message comes in
     *
     * @param {Object} message DOM PUSH message
     *
     * @private
     */
    callback: function (message) {

        if (push.handlers.hasOwnProperty(message.attribute)) {
            push[push.handlers[message.attribute]](message);
        }
    },

    /**
     * PUSH handlers available per attribute type
     *
     * @type {Object}
     */
    handlers: {
        'betInRun': 'betInRunUpdateCase',
        'displayed': 'displayedUpdateCase',
    },

    /**
     * Handles the dom push message containing an display attribute
     * The name of this function is linked to push.handlers
     *
     * @param  {Object} message PUSH message
     *
     * @return {void}
     *
     * @private
     */
    displayedUpdateCase: function (message) {

        if (!message.value && message.nodeType === 'events') {
            var $event = BasePage.jQuery('#' + message.id);
            removeMarketGroup($event);
        }

    },

    /**
     * Handles the dom push message containing an betInRun attribute
     * The name of this function is linked to push.handlers
     *
     * @param  {Object} message PUSH message
     *
     * @return {void}
     *
     * @private
     */
    betInRunUpdateCase: function (message) {

        var $event = BasePage.jQuery('#' + message.id);

        BasePage.propagateTopBetsBetInRun(message);

        if ($event.find('.alternate-market').length === 0) {
            hideInPlayEvent($event, message);
        } else {
            $event.attr('data-betinrun', message.value);
            showAlternateMarket($event, message);
            handleLivescore($event, message);
        }
    },
};

/**
 * Contains functions exposed by this component
 * as an API
 *
 * @type {Object}
 */
var api = {

    /**
     * Return the URL associated with this page
     * based on the args
     *
     * @param   {Object} args Page arguments
     *
     * @returns {String}      Compiled URL based on args
     *
     * @public
     */
    getContentURL: function (args) {

        return '/betting/' + args.locale + '/' + args.sport;
    },

    /**
     * Function called after the DOM ready event
     * is fired
     *
     * @param   {Object} deps Object that contains page
     *                        dependencies
     *
     * @returns {void}
     *
     * @public
     */
    postReady: function (deps, content, args) {

        BodyStateAdapter.init(BasePage.jQuery, 'highlights', args.sport);

        if (deps.ready) {
            deps.ready('highlights');
        }
    },

    /**
     * Function called after rendering the page
     *
     * @param   {Object} deps       Object that contains page
     *                              dependencies
     * @param   {Object} content    jQuery object that contains
     *                              this page
     *
     * @returns {void}
     *
     * @public
     */
    postRender: function (deps, content) {

        BasePage.switchTab('Highlights');
        Tabs.init();

        initViews(deps, content);

        SelectionViewAdapter.init(
            deps.sportsDataRepository.getModel().selections,
            content
        );

        _.each(
            deps.sportsDataRepository.getModel(),
            subscribeSetToPush
        );

        FirstTimeUserJourney.init(FirstTimeUserJourney.getPages().HIGHLIGHTS, args.sport);
    },

    bindNavigation: bindNavigation,

    /**
     * Changes rendered state flag to true
     *
     * @public
     */
    setRendered: function () {

        rendered = true;
    },

    /**
     * Changes rendered state flag to false
     *
     * @public
     */
    setUnrendered: function () {

        content = null;
        deps = null;
        args = null;
        rendered = false;
    },

    /**
     * Returns the value of the internal
     * rendered state flag
     *
     * @returns {Boolean} Returns true or false
     *
     * @public
     */
    isRendered: function () {

        return rendered;
    },

    /**
     * Slug to identify the current page
     */
    id: 'highlights',

};

export default api;
